import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { faqs } from "@/data/faqs";
import { langFromPathname, stripLangPrefix } from "@/i18n/LanguageProvider";


const SITE_URL = "https://woozy.energy";
const SITE_NAME = "WoozyAI";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg?v=3`;

type TrustedScriptPolicy = {
  createScript: (value: string) => string;
};

type TrustedTypesWindow = Window & {
  trustedTypes?: {
    createPolicy: (name: string, policy: TrustedScriptPolicy) => TrustedScriptPolicy;
  };
};

let trustedScriptPolicy: TrustedScriptPolicy | null = null;

type SeoConfig = {
  title: string;
  description: string;
  path: string;
  keywords: string;
  noindex?: boolean;
};

const pages: Record<string, SeoConfig> = {
  "/": {
    path: "/",
    title: "Solar Revenue Recovery & Storage Performance Optimization | WoozyAI",
    description:
      "Recover lost revenue across utility-scale solar, commercial rooftop, and solar-plus-storage portfolios. WoozyAI detects underperformance, optimizes storage performance, prioritizes high-value action, and measures financial recovery.",
    keywords:
      "WoozyAI, solar revenue recovery, storage performance optimization, solar asset management, utility-scale solar, commercial rooftop solar, solar plus storage, solar performance analytics, battery storage optimization",
  },

  "/how-it-works": {
    path: "/how-it-works",
    title: "How It Works | WoozyAI Solar Loss Detection",
    description:
      "See how WoozyAI connects operational data, models expected performance, verifies losses, quantifies financial impact, and measures recovery.",
    keywords:
      "expected solar production model, solar loss detection, energy performance benchmark, solar root cause analysis, storage performance analysis",
  },
  "/platform": {
    path: "/platform",
    title: "Platform Capabilities | WoozyAI",
    description:
      "WoozyAI capabilities across solar revenue recovery, storage optimization, O&M action intelligence, carbon value, ESG intelligence, and integrations.",
    keywords:
      "solar analytics platform, O&M prioritization software, storage performance software, ESG reporting workflow, solar data integration",
  },
  "/solutions": {
    path: "/solutions",
    title: "Solutions by Outcome | WoozyAI",
    description:
      "Outcome-focused solutions for solar revenue recovery, storage optimization, carbon value recovery, ESG intelligence, and O&M action intelligence.",
    keywords:
      "solar revenue recovery, storage optimization, carbon value recovery, ESG intelligence, O&M action intelligence",
  },
  "/solutions/asset-owners": {
    path: "/solutions/asset-owners",
    title: "Solar Revenue Recovery | WoozyAI",
    description:
      "Find where solar performance is becoming lost revenue across utility-scale, commercial rooftop, and solar-plus-storage portfolios.",
    keywords:
      "solar revenue recovery, solar underperformance, solar asset management, multi-site solar operations, lost kWh detection",
  },
  "/solutions/developers": {
    path: "/solutions/developers",
    title: "Solar Developers & IPPs | WoozyAI",
    description:
      "Prepare solar and storage projects for operations and capital with verified performance evidence, workflows, and traceable reporting.",
    keywords:
      "solar developers, IPP software, solar project operations, performance evidence, solar asset handover",
  },
  "/solutions/storage": {
    path: "/solutions/storage",
    title: "Storage Performance Optimization | WoozyAI",
    description:
      "Improve the operational and financial value of every stored kWh with availability, efficiency, utilization, and tariff-aware analysis.",
    keywords:
      "battery storage optimization, BESS performance analytics, solar plus storage, peak shaving analysis, storage efficiency",
  },
  "/solutions/carbon-value": {
    path: "/solutions/carbon-value",
    title: "Carbon Value Recovery | WoozyAI",
    description:
      "Measure clean generation, estimate avoided emissions, and prepare documented evidence for eligible environmental-value opportunities.",
    keywords:
      "carbon value recovery, avoided emissions solar, carbon credit documentation, grid emissions factors, environmental attributes",
  },
  "/solutions/esg": {
    path: "/solutions/esg",
    title: "ESG Intelligence | WoozyAI",
    description:
      "Produce traceable, framework-aligned ESG intelligence from operational data with data lineage, assumptions, and audit-ready evidence.",
    keywords:
      "ESG reporting solar, Scope 2 emissions reporting, audit-ready ESG evidence, ESG data lineage, renewable ESG intelligence",
  },
  "/solutions/om-intelligence": {
    path: "/solutions/om-intelligence",
    title: "O&M Action Intelligence | WoozyAI",
    description:
      "Turn alert noise into financially prioritized action with root-cause context, ownership, resolution tracking, and measured recovery.",
    keywords:
      "solar O&M software, alert prioritization, solar work order workflow, root cause analysis solar, maintenance prioritization",
  },
  "/validation-methodology": {
    path: "/validation-methodology",
    title: "Validation & Methodology | WoozyAI",
    description:
      "How WoozyAI models expected production, validates losses, quantifies financial impact, measures recovery, and states its limitations.",
    keywords:
      "solar performance methodology, weather normalization, lost kWh calculation, false positive controls, KPI definitions",
  },
  "/solar-asset-management-software": {
    path: "/solar-asset-management-software",
    title: "Solar Asset Management Software | WoozyAI",
    description:
      "Solar asset management software that verifies energy losses, ranks them by revenue impact, and tracks recovery across solar and storage portfolios.",
    keywords:
      "solar asset management software, solar asset management platform, solar portfolio management software, PV asset management, solar performance management software",
  },
  "/integrations": {
    path: "/integrations",
    title: "Integrations & Data Sources | WoozyAI",
    description:
      "Data sources and connector status across inverters, SCADA, BMS and EMS, weather, satellite, financial data, CMMS, and reporting exports.",
    keywords:
      "solar data integration, Modbus OPC UA solar, SCADA integration, EMS BMS integration, CMMS integration",
  },
  "/project-capital": {
    path: "/project-capital",
    title: "Project Capital | WoozyAI Verified Asset Evidence",
    description:
      "Verified solar and storage asset performance evidence for lenders, project equity investors, and asset managers.",
    keywords:
      "solar project capital, renewable energy lenders, solar equity investors, asset performance evidence, bankable solar data",
  },
  "/resources": {
    path: "/resources",
    title: "Resources & FAQs | WoozyAI",
    description:
      "Documentation, methodology, integration status, and answers about data requirements, loss verification, storage, carbon, and ESG outputs.",
    keywords:
      "solar analytics documentation, solar FAQ, methodology, data governance, WoozyAI resources",
  },
  "/about": {
    path: "/about",
    title: "About WoozyAI | Solar & Storage Intelligence",
    description:
      "WoozyAI is built at the intersection of AI, solar operations, energy infrastructure, and commercial execution.",
    keywords:
      "about WoozyAI, solar AI company, energy storage intelligence, solar operations team",
  },
  "/contact": {
    path: "/contact",
    title: "Book a Portfolio Review | WoozyAI",
    description:
      "Tell us about your portfolio, systems, and priorities. We tailor the session around your solar, storage, O&M, carbon, or ESG environment.",
    keywords:
      "solar portfolio review, WoozyAI demo, solar analytics demo, storage analytics demo",
  },
  "/accessibility-statement": {
    path: "/accessibility-statement",
    title: "Accessibility Statement | WoozyAI",
    description:
      "WoozyAI accessibility statement and information about website accessibility support.",
    keywords: "WoozyAI accessibility, accessibility statement, WCAG accessibility",
  },
  "/privacy-policy": {
    path: "/privacy-policy",
    title: "Privacy Policy | WoozyAI",
    description:
      "How WoozyAI collects, uses, stores, and protects personal and operational data across its solar and storage intelligence platform and website.",
    keywords: "WoozyAI privacy policy, data protection, personal data, GDPR",
  },
  "/terms-of-service": {
    path: "/terms-of-service",
    title: "Terms of Service | WoozyAI",
    description:
      "The terms that govern use of the WoozyAI website and platform, including acceptable use, intellectual property, liability, and account obligations.",
    keywords: "WoozyAI terms of service, terms and conditions, acceptable use",
  },
  "/cookie-policy": {
    path: "/cookie-policy",
    title: "Cookie Policy | WoozyAI",
    description:
      "How WoozyAI uses cookies and similar technologies on its website, what each category does, and how you can manage your cookie preferences.",
    keywords: "WoozyAI cookie policy, cookies, tracking preferences",
  },

  "/unsubscribe": {
    path: "/unsubscribe",
    title: "Email Preferences | WoozyAI",
    description:
      "Manage your WoozyAI email preferences. Update or unsubscribe from WoozyAI product, platform, and portfolio-review communications at any time.",
    keywords: "WoozyAI email preferences",
    noindex: true,
  },

};

const defaultPage: SeoConfig = {
  path: "/404",
  title: "Page Not Found | WoozyAI",
  description: "The requested WoozyAI page could not be found.",
  keywords: "WoozyAI",
  noindex: true,
};

const setMeta = (selector: string, attr: "content" | "href", value: string, create?: () => HTMLElement) => {
  let element = document.head.querySelector(selector) as HTMLElement | null;
  if (!element && create) {
    element = create();
    document.head.appendChild(element);
  }
  element?.setAttribute(attr, value);
};

const upsertNameMeta = (name: string, content: string) =>
  setMeta(`meta[name="${name}"]`, "content", content, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", name);
    return meta;
  });

const upsertPropertyMeta = (property: string, content: string) =>
  setMeta(`meta[property="${property}"]`, "content", content, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", property);
    return meta;
  });

const upsertCanonical = (href: string) =>
  setMeta('link[rel="canonical"]', "href", href, () => {
    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    return link;
  });

/**
 * Hebrew metadata overrides. Any route not listed here falls back to the
 * English metadata, so /he pages always have a valid title and description.
 */
const hebrewPages: Record<string, Partial<SeoConfig>> = {
  "/": {
    title: "ניטור ותחזוקת מערכות סולאריות ואגירת אנרגיה | WoozyAI",
    description:
      "WoozyAI מזהה תת-ביצוע במערכות סולאריות, מאתרת אובדן הכנסות, מתעדפת תחזוקה לפי השפעה כספית ומייעלת מערכות אגירת אנרגיה - בשדות סולאריים בקנה מידה יוטיליטי ובגגות מסחריים בישראל.",
    keywords:
      "ניטור מערכות סולאריות, תחזוקת מערכות סולאריות, ניהול נכסים סולאריים, אגירת אנרגיה, מערכות אגירת אנרגיה, אנרגיה מתחדשת ישראל, שדה סולארי, גג סולארי מסחרי, תפעול ותחזוקה סולארי, השבת הכנסות סולאריות, WoozyAI",
  },
  "/how-it-works": {
    title: "איך זה עובד | WoozyAI",
    description:
      "כיצד WoozyAI מחברת נתונים תפעוליים, מודדת ביצועים צפויים, מאמתת אובדן אנרגיה, מכמתת השפעה כספית ומודדת השבה.",
    keywords: "זיהוי אובדן אנרגיה סולארית, ניתוח שורש בעיה, מודל ייצור צפוי, ניתוח ביצועי אגירה",
  },
  "/platform": {
    title: "פלטפורמה לניהול ותפעול מערכות סולאריות ואגירה | WoozyAI",
    description:
      "פלטפורמת WoozyAI לניטור, ניהול ותחזוקת מערכות סולאריות ואגירת אנרגיה: זיהוי תקלות ואובדן ייצור, תעדוף עבודות תפעול ותחזוקה, אופטימיזציית אגירה, דיווח ESG ואינטגרציה ל-SCADA ואינוורטרים.",
    keywords:
      "פלטפורמת ניטור סולארי, מערכת לניהול נכסים סולאריים, תוכנה לתפעול ותחזוקה סולארי, אופטימיזציה של אגירת אנרגיה, ניטור אינוורטרים, אינטגרציית SCADA, דוחות ייצור סולארי, אנרגיה סולארית ישראל",
  },

  "/solutions": {
    title: "פתרונות לפי תוצאה עסקית | WoozyAI",
    description:
      "פתרונות ממוקדי תוצאה להשבת הכנסות סולאריות, אופטימיזציה של אגירה, מימוש ערך פחמן, אינטליגנציית ESG ואינטליגנציית O&M.",
    keywords: "השבת הכנסות סולאריות, אופטימיזציה של אגירה, ערך פחמן, ESG, O&M",
  },
  "/about": {
    title: "אודות WoozyAI",
    description:
      "WoozyAI היא שכבת AI Agents as a Service להשבת הכנסות וביצועים אוטונומיים בתיקי נכסי סולאר ואגירה.",
    keywords: "WoozyAI, אודות, AI Agents as a Service, אנרגיה סולארית, אגירה",
  },
  "/contact": {
    title: "צור קשר ותיאום דמו | WoozyAI",
    description: "דברו עם צוות WoozyAI או בקשו דמו של שכבת השבת ההכנסות לתיקי סולאר ואגירה.",
    keywords: "WoozyAI צור קשר, תיאום דמו, אנרגיה סולארית, אגירה",
  },
  "/project-capital": {
    title: "מימון פרויקטים | WoozyAI",
    description: "ראיות ביצועים מאומתות ותהליכי דיווח לתומכי פרויקטים, מלווים ומשקיעים בסולאר ואגירה.",
    keywords: "מימון פרויקטים סולאריים, ראיות ביצועים, בנקביליות, דיווח למלווים",
  },
  "/resources": {
    title: "מקורות מידע ושאלות נפוצות | WoozyAI",
    description: "חומרים, מתודולוגיה ושאלות נפוצות על השבת הכנסות סולאריות ואופטימיזציה של אגירה.",
    keywords: "שאלות נפוצות סולאר, מתודולוגיה, מקורות מידע WoozyAI",
  },
  "/integrations": {
    title: "אינטגרציות נתונים | WoozyAI",
    description: "חיבור SCADA, אינוורטרים, מטרים, מערכות אגירה ומקורות נתוני מזג אוויר לשכבת ביצועים אחת.",
    keywords: "אינטגרציית SCADA, נתוני אינוורטרים, נתוני אגירה, API",
  },
  "/privacy-policy": { title: "מדיניות פרטיות | WoozyAI" },
  "/terms-of-service": { title: "תנאי שימוש | WoozyAI" },
  "/cookie-policy": { title: "מדיניות קובצי Cookie | WoozyAI" },
  "/accessibility-statement": { title: "הצהרת נגישות | WoozyAI" },
};

const upsertAlternates = (enUrl: string, heUrl: string) => {
  document.querySelectorAll('link[rel="alternate"][data-woozyai-hreflang="true"]').forEach((node) => node.remove());

  const alternates: Array<[string, string]> = [
    ["en", enUrl],
    ["he", heUrl],
    ["x-default", enUrl],
  ];

  alternates.forEach(([hrefLang, href]) => {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hrefLang);
    link.setAttribute("href", href);
    link.setAttribute("data-woozyai-hreflang", "true");
    document.head.appendChild(link);
  });
};

const buildStructuredData = (page: SeoConfig, canonicalUrl: string, lang: "en" | "he") => {

  const base: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/woozyai-icon-512.png`,
      sameAs: ["https://www.linkedin.com/company/woozyai/"],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: SITE_NAME,
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: lang,
    },
  ];

  if (page.path === "/") {
    base.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/#software`,
      name: SITE_NAME,
      url: SITE_URL,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: page.description,
      publisher: { "@id": `${SITE_URL}/#organization` },
    } as Record<string, unknown>);
  }

  if (page.path === "/resources") {
    base.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${canonicalUrl}#faq`,
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    } as Record<string, unknown>);
  }

  if (page.path === "/solar-asset-management-software") {
    base.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${canonicalUrl}#faq`,
      mainEntity: faqs.slice(0, 4).map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    } as Record<string, unknown>);
  }



  base.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: page.title,
    description: page.description,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage: lang,
  } as Record<string, unknown>);

  return base;
};


const SEO = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const lang = langFromPathname(pathname);
    const basePath = stripLangPrefix(pathname);
    const normalizedPath = basePath.replace(/\/$/, "") || "/";
    const englishPage = pages[normalizedPath] || defaultPage;
    const hebrewOverride = lang === "he" ? hebrewPages[normalizedPath] : undefined;
    const page: SeoConfig = { ...englishPage, ...(hebrewOverride ?? {}) };

    const pathSuffix = englishPage.path === "/" ? "" : englishPage.path;
    const enUrl = `${SITE_URL}${pathSuffix}`;
    const heUrl = `${SITE_URL}/he${pathSuffix}`;
    const canonicalUrl = lang === "he" ? heUrl : enUrl;

    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "he" ? "rtl" : "ltr";
    document.title = page.title;

    upsertNameMeta("description", page.description);
    upsertNameMeta("keywords", page.keywords);
    upsertNameMeta("author", SITE_NAME);
    upsertNameMeta("robots", page.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertNameMeta("theme-color", "#050816");

    upsertCanonical(canonicalUrl);
    upsertAlternates(enUrl, heUrl);

    upsertPropertyMeta("og:type", "website");
    upsertPropertyMeta("og:site_name", SITE_NAME);
    upsertPropertyMeta("og:locale", lang === "he" ? "he_IL" : "en_US");
    upsertPropertyMeta("og:locale:alternate", lang === "he" ? "en_US" : "he_IL");
    upsertPropertyMeta("og:title", page.title);
    upsertPropertyMeta("og:description", page.description);
    upsertPropertyMeta("og:url", canonicalUrl);
    upsertPropertyMeta("og:image", DEFAULT_IMAGE);
    upsertPropertyMeta("og:image:alt", "WoozyAI solar and storage revenue recovery platform");



    upsertNameMeta("twitter:card", "summary_large_image");
    upsertNameMeta("twitter:title", page.title);
    upsertNameMeta("twitter:description", page.description);
    upsertNameMeta("twitter:image", DEFAULT_IMAGE);
    upsertNameMeta("twitter:image:alt", "WoozyAI solar and storage revenue recovery platform");

    const structuredDataId = "woozyai-json-ld";
    let script = document.getElementById(structuredDataId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = structuredDataId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    const structuredData = JSON.stringify(buildStructuredData(page, canonicalUrl, lang));
    const trustedTypes = (window as TrustedTypesWindow).trustedTypes;
    if (trustedTypes) {
      if (!trustedScriptPolicy) {
        try {
          trustedScriptPolicy = trustedTypes.createPolicy("woozyai-json-ld", {
            createScript: (value) => value,
          });
        } catch {
          trustedScriptPolicy = null;
        }
      }
    }
    if (trustedScriptPolicy) {
      script.text = trustedScriptPolicy.createScript(structuredData) as unknown as string;
    } else {
      script.textContent = structuredData;
    }
  }, [pathname]);

  return null;
};

export default SEO;
